import React, { FunctionComponent, memo, useRef, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import { Box, IconButton } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as CloseIcon } from "@assets/svg/close.svg";
import { Nullable } from "@shared/models/general";

interface DatepickerProps {
  value: string;
  onChange(value: Nullable<Date>): void;
}

const startOfToday = (): Date => {
  const d = new Date();

  d.setHours(0, 0, 0, 0);

  return d;
};

const useStyles = makeStyles((theme) => ({
  root: {
    height: 55,
    padding: theme.spacing(0, 5),
    backgroundColor: theme.palette.common.white,
    cursor: "pointer",
    color: theme.palette.primary.main,
    fontSize: 16,
    fontWeight: theme.typography.fontWeightMedium,
    display: "flex",
    alignItems: "center",
    position: "relative" as const,
  },
  clearButton: {
    position: "absolute" as const,
    right: 8,
    padding: 4,
  },
}));

const TextField = ({ inputProps, value, label, id, setOpen, onClear, showClear }: any) => {
  const classes = useStyles();

  return (
    <Box
      id={id}
      className={classes.root}
      data-testid="datepicker-toggle-button"
      ref={inputProps!.ref}
      onClick={() => setOpen((open: boolean) => !open)}
    >
      {value || label}
      {showClear && value && (
        <IconButton
          className={classes.clearButton}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          data-testid="datepicker-clear-button"
        >
          <CloseIcon style={{ width: 18, height: 18 }} />
        </IconButton>
      )}
    </Box>
  );
};

const InvestorRequestDatepicker: FunctionComponent<DatepickerProps> = ({ value, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);

  const handleClear = () => {
    onChange(null);
  };

  return (
    <DatePicker
      open={open}
      inputRef={inputRef}
      value={value ? new Date(value) : null}
      minDate={startOfToday()}
      orientation="landscape"
      format="MMMM d, yyyy"
      label="Date Instructions Become Effective"
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      onChange={onChange}
      slots={{ textField: TextField }}
      slotProps={{
        textField: { setOpen, onClear: handleClear, showClear: true } as any,
        popper: { anchorEl: inputRef.current, placement: "bottom" },
      }}
    />
  );
};

export default memo<DatepickerProps>(InvestorRequestDatepicker);
